import { MenuOutlined, BellOutlined, BellFilled } from "@ant-design/icons";
import { Button, Drawer, Grid, Menu, Switch } from "antd";
import { AuthContext } from "context/AuthContext";
import React, { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import useInteractiveNotifications from "hooks/useInteractiveNotifications";
import { Link, useMatch, useNavigate, useResolvedPath } from "react-router-dom";
import SkydevLogo from "./../../../assets/images/obc-logo.png";
import LanguageSwitcher from "../common/LanguageSwitcher";
import "./Header.less";

const { useBreakpoint } = Grid;

const CustomLink = ({ children, to, color, ...props }) => {
  let resolved = useResolvedPath(to);
  let match = useMatch({ path: resolved.pathname, end: true });

  return (
    <Link
      className={match ? "nav-link nav-active" : "nav-link"}
      to={to}
      {...props}
    >
      {children}
    </Link>
  );
};

const Header = ({ glassTheme = "default", ...props }) => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const screens = useBreakpoint();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const { t } = useTranslation();
  const { isDisplayToast, setIsDisplayToast } = useInteractiveNotifications();

  const goLogin = () => navigate("/login");
  const goLogout = useCallback(() => {
    logout();
    navigate("/");
  }, [logout, navigate]);

  const closeDrawer = () => setDrawerVisible(false);

  const getGlassThemeClass = () => {
    switch (glassTheme) {
      case "dark":
        return "custom-drawer glass-dark";
      case "light":
        return "custom-drawer glass-light";
      default:
        return "custom-drawer";
    }
  };

  const menuDrawerItems = (
    <Menu
      style={{ backgroundColor: "transparent" }}
      mode="vertical"
      onClick={closeDrawer}
    >
      {user?.userName && (
        <Menu.Item key="cart-private">
          <CustomLink color="#000000" to="/cart-private">
            {t("header.nav.public-cart")}
          </CustomLink>
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <header id="header" className="animate__animated animate__fadeIn">
      <div className="container">
        <div className="header">
          <div className="logo">
            <CustomLink className="navbar-brand" to="/">
              <img className="logo_img" src={SkydevLogo} alt="OBC Logo" />
            </CustomLink>
          </div>

          {screens.md ? (
            <div
              className="navigation"
              style={{ display: "flex", alignItems: "center", gap: "20px" }}
            >
              <ul
                className="navbar-nav"
                style={{
                  display: "flex",
                  listStyle: "none",
                  gap: "16px",
                  margin: 0,
                  padding: 0,
                }}
              >
                {user?.userName && (
                  <li className="nav-item">
                    <CustomLink to="/cart-private">
                      {t("header.nav.public-cart")}
                    </CustomLink>
                  </li>
                )}
              </ul>
              {user?.userName ? (
                <Button className="header-button" onClick={goLogout}>
                  {t("header.nav.logout")}
                </Button>
              ) : (
                <Button className="header-button" onClick={goLogin}>
                  {t("header.nav.signin")}
                </Button>
              )}
              <div className="notification-switcher">
                <Switch
                  checked={!!isDisplayToast}
                  onChange={(checked) => setIsDisplayToast(checked)}
                  checkedChildren="On"
                  unCheckedChildren="Off"
                />
                {isDisplayToast ? (
                  <BellFilled style={{ fontSize: "20px", color: "#52c41a" }} />
                ) : (
                  <BellOutlined style={{ fontSize: "20px", color: "#999" }} />
                )}
              </div>
              <p className="nav-username">
                <span>{user.userName}</span>
              </p>
              {LanguageSwitcher()}
            </div>
          ) : (
            <>
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setDrawerVisible(true)}
                style={{ color: "white" }}
              />
              <Drawer
                placement="right"
                width="70%"
                onClose={closeDrawer}
                open={drawerVisible}
                rootClassName={getGlassThemeClass()}
                className={getGlassThemeClass()}
                maskClosable={true}
                // Remove the inline styles to let CSS handle the glass effect
                styles={{
                  mask: {
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                    backdropFilter: "blur(4px)",
                  },
                }}
                // Custom header with glass effect
                title={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      color: "rgba(255, 255, 255, 0.9)",
                    }}
                  >
                    <img
                      className="drawer-logo"
                      src={SkydevLogo}
                      alt="logo"
                      style={{ height: "30px" }}
                    />
                  </div>
                }
              >
                {/* Language Switcher */}
                <div style={{ marginBottom: "20px" }}>{LanguageSwitcher()}</div>

                {/* Menu Items */}
                {menuDrawerItems}

                {/* Login/Logout Button */}
                <div style={{ marginTop: "20px" }}>
                  {user?.userName ? (
                    <Button
                      type="primary"
                      onClick={goLogout}
                      danger
                      block
                      size="large"
                    >
                      {t("header.nav.logout")}
                    </Button>
                  ) : (
                    <Button type="primary" onClick={goLogin} block size="large">
                      {t("header.nav.signin")}
                    </Button>
                  )}
                </div>
              </Drawer>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
